// Tiling detail maps for the battlefield ground.
//
// The ground was a single flat plane with one 2048px colour canvas stretched over a 270x180 m
// map — roughly 7.6 texels per metre, so at close zoom it is visibly soft and completely flat:
// grep for normalMap/roughnessMap across src/render before this change returns nothing.
//
// These are small *tiling* maps layered on top of that colour map. three gives every texture
// slot its own UV transform (the shader generates vNormalMapUv, vRoughnessMapUv, ... from each
// texture's own repeat/offset), so the big albedo and a heavily-repeated detail map coexist with
// no custom shader and no second UV set.
//
// Generated at runtime, matching the existing makeGroundTexture idiom — no binary assets.

import * as THREE from 'three';

const DETAIL_SIZE = 512;

/**
 * Deterministic value noise. Uses a hashed integer lattice rather than Math.random so the maps
 * are stable across reloads (the render layer may use Math.random, but stable ground avoids
 * "why does the dirt look different this match" reports).
 */
function makeNoise(seed: number) {
  const hash = (x: number, y: number): number => {
    let h = x * 374761393 + y * 668265263 + seed * 2246822519;
    h = (h ^ (h >>> 13)) >>> 0;
    h = (h * 1274126177) >>> 0;
    return (h ^ (h >>> 16)) / 4294967295;
  };
  const smooth = (t: number) => t * t * (3 - 2 * t);
  return (x: number, y: number): number => {
    const x0 = Math.floor(x);
    const y0 = Math.floor(y);
    const fx = smooth(x - x0);
    const fy = smooth(y - y0);
    const a = hash(x0, y0);
    const b = hash(x0 + 1, y0);
    const c = hash(x0, y0 + 1);
    const d = hash(x0 + 1, y0 + 1);
    return a + (b - a) * fx + (c - a) * fy + (a - b - c + d) * fx * fy;
  };
}

/** Seamless fbm over a period-P lattice, so the generated tile wraps without a visible seam. */
function fbm(noise: (x: number, y: number) => number, x: number, y: number, period: number): number {
  let value = 0;
  let amplitude = 0.5;
  let frequency = 1;
  for (let octave = 0; octave < 4; octave++) {
    // Wrapping the lattice coordinates at each octave is what makes the tile seamless.
    const p = period * frequency;
    value += amplitude * noise(((x * frequency) % p + p) % p, ((y * frequency) % p + p) % p);
    amplitude *= 0.5;
    frequency *= 2;
  }
  return value;
}

export interface GroundDetailMaps {
  normalMap: THREE.CanvasTexture;
  roughnessMap: THREE.CanvasTexture;
  dispose(): void;
}

/**
 * Builds a matched normal + roughness pair from one height field, so the bumps and the sheen
 * agree. Repeat is set by the caller from the map size.
 */
export function makeGroundDetailMaps(): GroundDetailMaps {
  const size = DETAIL_SIZE;
  const lattice = 16; // noise cells across the tile
  const noise = makeNoise(0x51617);

  // ---- height field
  const height = new Float32Array(size * size);
  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      const u = (x / size) * lattice;
      const v = (y / size) * lattice;
      // Two scales: broad clumping plus fine grain, so it reads as soil and not as static.
      const broad = fbm(noise, u, v, lattice);
      const fine = fbm(noise, u * 3.7, v * 3.7, lattice * 3.7);
      height[y * size + x] = broad * 0.72 + fine * 0.28;
    }
  }

  // ---- normal map, Sobel-filtered from the height field
  const normalCanvas = document.createElement('canvas');
  normalCanvas.width = size;
  normalCanvas.height = size;
  const normalCtx = normalCanvas.getContext('2d')!;
  const normalData = normalCtx.createImageData(size, size);
  // Wrapping index so the Sobel kernel is seamless at the tile edges.
  const at = (x: number, y: number) => height[((y + size) % size) * size + ((x + size) % size)];
  const strength = 2.6; // TEST — perceived relief depth
  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      const dx =
        at(x - 1, y - 1) + 2 * at(x - 1, y) + at(x - 1, y + 1) -
        (at(x + 1, y - 1) + 2 * at(x + 1, y) + at(x + 1, y + 1));
      const dy =
        at(x - 1, y - 1) + 2 * at(x, y - 1) + at(x + 1, y - 1) -
        (at(x - 1, y + 1) + 2 * at(x, y + 1) + at(x + 1, y + 1));
      // Tangent-space normal: +Z out of the surface, normalised into 0..255.
      let nx = dx * strength;
      let ny = dy * strength;
      const nz = 1;
      const inv = 1 / Math.hypot(nx, ny, nz);
      nx *= inv;
      ny *= inv;
      const index = (y * size + x) * 4;
      normalData.data[index] = (nx * 0.5 + 0.5) * 255;
      normalData.data[index + 1] = (ny * 0.5 + 0.5) * 255;
      normalData.data[index + 2] = nz * inv * 255;
      normalData.data[index + 3] = 255;
    }
  }
  normalCtx.putImageData(normalData, 0, 0);

  // ---- roughness map from the same field: hollows hold moisture and read smoother/darker,
  // raised grain is dry and rougher. Keeps the sheen correlated with the relief.
  const roughCanvas = document.createElement('canvas');
  roughCanvas.width = size;
  roughCanvas.height = size;
  const roughCtx = roughCanvas.getContext('2d')!;
  const roughData = roughCtx.createImageData(size, size);
  for (let i = 0; i < size * size; i++) {
    const h = height[i];
    const rough = Math.max(0, Math.min(1, 0.62 + (h - 0.5) * 0.5)); // TEST
    const value = rough * 255;
    const index = i * 4;
    roughData.data[index] = value;
    roughData.data[index + 1] = value;
    roughData.data[index + 2] = value;
    roughData.data[index + 3] = 255;
  }
  roughCtx.putImageData(roughData, 0, 0);

  const normalMap = new THREE.CanvasTexture(normalCanvas);
  normalMap.wrapS = THREE.RepeatWrapping;
  normalMap.wrapT = THREE.RepeatWrapping;
  // Normal and roughness maps are data, not colour — they must stay linear.
  normalMap.colorSpace = THREE.NoColorSpace;

  const roughnessMap = new THREE.CanvasTexture(roughCanvas);
  roughnessMap.wrapS = THREE.RepeatWrapping;
  roughnessMap.wrapT = THREE.RepeatWrapping;
  roughnessMap.colorSpace = THREE.NoColorSpace;

  return {
    normalMap,
    roughnessMap,
    dispose() {
      normalMap.dispose();
      roughnessMap.dispose();
    },
  };
}

/** One detail tile every N metres. Lower = finer grain. // TEST */
export const GROUND_DETAIL_METRES = 6;
